/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { BASE_SERVICE_URI } from '../../constants/ApiConstants';
import { registerMutation, registerQuery, useMutation, useQuery } from 'academy-common-lib/utils/DataFetchingClient';
import { useCallback, useMemo } from 'react';
import { getQueryFieldName, queryDataFromCache, updateCache } from '../../utils/DataFetchingClientCache';
import { useUserInfo } from '../accounts/accountsQueries';
import http from 'hub-http/clients/apiClient';
import Raven from 'raven-js';
import { refetchMyPlaylists } from '../myPlaylists/myPlaylistsQueries';
import { useFilteredRestrictedPlaylistItems } from '../playlistItems/playlistItemsHooks';
import { GET_PLAYLIST_OR_LEARNING_PATH_QUERY } from '../../../__generated__/chirp/com/hubspot/learning/center/playlists/rpc/LearningPathChirpService.dfc';
import { GET_SIMPLE_LEARNING_PATHS_OR_PLAYLISTS_QUERY } from '../../../__generated__/chirp/com/hubspot/learning/center/playlists/rpc/LearningPathChirpService.dfc.new';
import { useCreatePlaylistMutation, useAddPlaylistItemMutation, useDeletePlaylistItemMutation } from '../../../__generated__/chirp/com/hubspot/learning/center/playlists/rpc/PlaylistChirpService.hooks';
// this is an awful hack that's needed to get the learning paths feature finished before EOY. We should fix the issue with the BE language type coming through wrong and remove this ASAP
const fixPlaylistItemsLanguage = playlist => {
  var _playlist$items;
  return Object.assign({}, playlist, {
    items: ((_playlist$items = playlist.items) === null || _playlist$items === void 0 ? void 0 : _playlist$items.map(item => Object.assign({}, item, {
      language: item.language.toString().toLowerCase()
    }))) || []
  });
};
export const PLAYLISTS_API = `${BASE_SERVICE_URI}/playlists/v1`;
export const PLAYLISTS_ITEMS_URL_FRAGMENT = `/items`;
export const GET_PLAYLIST = registerQuery({
  fieldName: 'playlist',
  args: ['playlistId'],
  fetcher({
    playlistId
  }) {
    return http.get(`${PLAYLISTS_API}/${playlistId}`);
  }
});
export const updatePlaylistFromCache = (playlistId, playlist) => {
  const variables = {
    playlistId
  };
  const currentPlaylist = queryDataFromCache(GET_PLAYLIST_OR_LEARNING_PATH_QUERY, variables);
  if (currentPlaylist) {
    const updatedPlaylist = playlist ? Object.assign({}, currentPlaylist, playlist) : null;
    updateCache(GET_PLAYLIST_OR_LEARNING_PATH_QUERY, () => updatedPlaylist, variables);
  }
};
export const updatePlaylistItemsInCache = ({
  playlistId,
  items
}) => {
  const variables = {
    playlistId
  };
  const currentPlaylist = queryDataFromCache(GET_PLAYLIST_OR_LEARNING_PATH_QUERY, variables);
  if (currentPlaylist && items) {
    const updatedPlaylist = Object.assign({}, currentPlaylist, {
      items
    });
    updateCache(GET_PLAYLIST_OR_LEARNING_PATH_QUERY, () => updatedPlaylist, variables);
  }
};
export const usePlaylist = (playlistId, skip = false) => {
  const {
    data,
    loading: playlistLoading,
    error: playlistError
  } = useQuery(GET_PLAYLIST_OR_LEARNING_PATH_QUERY, {
    variables: {
      playlistId
    },
    skip
  });
  const playlist = useMemo(() => {
    if (data) {
      return fixPlaylistItemsLanguage(data);
    }
  }, [data]);
  return {
    playlist,
    playlistLoading,
    playlistError
  };
};

// the difference between a full playlist and a basic one is that the basic playlist
// has an empty list of items. The way to know if it is a full playlist
const isFullPlaylist = cachePlaylist => cachePlaylist && cachePlaylist.items && cachePlaylist.itemsCount === cachePlaylist.items.length;
export const useFullPlaylist = (playlistId, skip) => {
  const cachePlaylist = queryDataFromCache(GET_PLAYLIST_OR_LEARNING_PATH_QUERY, {
    playlistId
  });
  const {
    data,
    loading: playlistLoading,
    error: playlistError
  } = useQuery(GET_PLAYLIST_OR_LEARNING_PATH_QUERY, {
    variables: {
      playlistId
    },
    // in case we have the basic playlist (no items) in cache or it has not been cached
    // we want to request the full playlist and update the cache with it
    fetchPolicy: isFullPlaylist(cachePlaylist) ? 'cache-only' : 'network-only',
    skip
  });
  const playlist = useMemo(() => {
    if (data) {
      return fixPlaylistItemsLanguage(data);
    }
  }, [data]);
  return {
    playlist,
    playlistLoading,
    playlistError,
    displayedPlaylistItems: useFilteredRestrictedPlaylistItems((playlist === null || playlist === void 0 ? void 0 : playlist.items) || [])
  };
};
export const useCreatePlaylist = ({
  onCompleted
} = {}) => {
  const [mutate, {
    loading: createPlaylistLoading,
    error: createPlaylistError
  }] = useCreatePlaylistMutation({
    onCompleted: data => {
      if (data !== null && data !== void 0 && data.result && onCompleted) {
        onCompleted(data.result);
      }
      // refetch doesn't work if we add an item to the recently created playlist.
      // Using manual eviction as an alternative
      refetchMyPlaylists();
    }
  });
  const createPlaylist = useCallback(({
    name,
    description,
    items
  }) => {
    return mutate({
      variables: {
        name,
        description,
        items
      }
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [mutate]);
  return {
    createPlaylist,
    createPlaylistLoading,
    createPlaylistError
  };
};
export const UPDATE_PLAYLIST = registerMutation({
  fieldName: 'updatePlaylist',
  args: ['playlistId', 'playlist'],
  fetcher({
    playlistId,
    playlist
  }) {
    if (playlist) return http.put(`${PLAYLISTS_API}/${playlistId}`, {
      data: playlist
    });else throw new Error(`Playlist ${playlistId} not loaded`);
  }
});
export const useUpdatePlaylist = (playlistId, {
  onCompleted,
  refetchQueries,
  onError
} = {
  onCompleted: () => {},
  onError: () => {},
  refetchQueries: []
}) => {
  const {
    playlist
  } = usePlaylist(playlistId);
  const [mutate, {
    loading: updatePlaylistMutationLoading,
    called: updatePlaylistMutationCalled,
    error: updatePlaylistMutationError
  }] = useMutation(UPDATE_PLAYLIST, {
    refetchQueries,
    onCompleted,
    onError,
    update(cache, result) {
      const updatedPlaylist = result.data && result.data.updatePlaylist;
      if (updatedPlaylist) {
        updatePlaylistFromCache(updatedPlaylist.id, updatedPlaylist);
      }

      // renew all the cached playlists to show the new thumbnail
      // TODO XAVI: This is like killing a fly with a cannon. We should find a more efficient way to do this like just updating the affected cache. Will work on this later.
      cache.evict({
        fieldName: getQueryFieldName(GET_SIMPLE_LEARNING_PATHS_OR_PLAYLISTS_QUERY)
      });
    }
  });
  const updatePlaylist = useCallback((updatedPlaylist, opts = {}) => {
    const newPlaylist = Object.assign({}, playlist, updatedPlaylist);
    return mutate({
      variables: {
        playlistId,
        playlist: newPlaylist
      },
      // @ts-expect-error updatePlaylist is type JSON
      optimisticResponse: opts.optimisticResponse ? {
        updatePlaylist: newPlaylist
      } : undefined
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [mutate, playlist, playlistId]);
  return [updatePlaylist, {
    updatePlaylistMutationLoading,
    updatePlaylistMutationCalled,
    updatePlaylistMutationError
  }];
};
export const useAddToPlaylist = () => {
  const [mutate, {
    loading: addToPlaylistMutationLoading,
    called: addToPlaylistMutationCalled,
    error: addToPlaylistMutationError
  }] = useAddPlaylistItemMutation({
    update: (cache, {
      data,
      errors
    }) => {
      if (!data || !data.result || errors && errors.length) return;
      updatePlaylistItemsInCache({
        playlistId: data.result.id,
        items: data.result.items
      });
    }
  });
  const addToPlaylist = useCallback(({
    playlistId,
    itemId,
    type,
    language
  }) => {
    return mutate({
      variables: {
        playlistId,
        playlistItemRequest: {
          itemId,
          type,
          language
        }
      }
    });
  }, [mutate]);
  return [addToPlaylist, {
    addToPlaylistMutationLoading,
    addToPlaylistMutationCalled,
    addToPlaylistMutationError
  }];
};
export const useRemoveFromPlaylist = () => {
  const [mutate, {
    loading: removeFromPlaylistMutationLoading,
    called: removeFromPlaylistMutationCalled,
    error: removeFromPlaylistMutationError
  }] = useDeletePlaylistItemMutation({
    update: (_, {
      data,
      errors
    }) => {
      if (!data || !data.result || errors && errors.length) return;
      updatePlaylistItemsInCache({
        playlistId: data.result.id,
        items: data.result.items
      });
    }
  });
  const removeFromPlaylist = useCallback(({
    playlistId,
    itemId,
    type,
    language
  }) => {
    return mutate({
      variables: {
        playlistId,
        playlistItemRequest: {
          itemId,
          type,
          language
        }
      }
    });
  }, [mutate]);
  return [removeFromPlaylist, {
    removeFromPlaylistMutationLoading,
    removeFromPlaylistMutationCalled,
    removeFromPlaylistMutationError
  }];
};
export const useUserOwnsPlaylist = playlistOrPlaylistId => {
  const isPlaylist = playlistOrPlaylistId && typeof playlistOrPlaylistId === 'object';
  const playlistId = isPlaylist ? playlistOrPlaylistId.id : playlistOrPlaylistId;
  const {
    userInfo
  } = useUserInfo();
  const {
    playlist: playlistFromQuery
  } = usePlaylist(playlistId, !!(!playlistId || isPlaylist));
  const playlist = isPlaylist ? playlistOrPlaylistId : playlistFromQuery;
  if (!userInfo || !playlist) return null;
  return playlist.ownerUser.userId === userInfo.user.id;
};
export const useSetPlaylistThumbnail = playlistId => {
  const [mutate, {
    updatePlaylistMutationLoading: setPlaylistThumbnailMutationLoading,
    updatePlaylistMutationCalled: setPlaylistThumbnailMutationCalled,
    updatePlaylistMutationError: setPlaylistThumbnailMutationError
  }] = useUpdatePlaylist(playlistId);
  const setPlaylistThumbnail = useCallback((thumbnailRectangleUrl, thumbnailSquareUrl) => {
    return mutate({
      thumbnailRectangleUrl: thumbnailRectangleUrl || undefined,
      thumbnailSquareUrl: thumbnailSquareUrl || undefined
    });
  }, [mutate]);
  return [setPlaylistThumbnail, {
    setPlaylistThumbnailMutationLoading,
    setPlaylistThumbnailMutationCalled,
    setPlaylistThumbnailMutationError
  }];
};